import React, { useState, useRef, Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import planQuestionStore from './store';
import { SVGPureIcon } from '@/ui';
import { runInAction } from 'mobx';

const numberToChinese = num => {
  var units = ['', '十', '百', '千', '万', '十万', '百万', '千万', '亿'];
  var digits = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  var result = '';

  var numArray = String(num).split('').reverse(); // 将数字转为倒序的数组
  for (var i = 0; i < numArray.length; i++) {
    var digit = numArray[i]; // 当前位的数字
    var unit = units[i]; // 当前位的单位

    // 对于0的处理
    if (digit === '0') {
      // 如果是连续的多个0，只保留一个零
      if (result[0] === '零') {
        result = result.substring(1);
      }
      // 如果不是最末尾的0，添加一个零
      if (i !== numArray.length - 1) {
        result = '零' + result;
      }
      continue;
    }

    result = digits[digit] + unit + result;
  }

  return result.replace('一十', '十').replace('十零', '十');
};

//设置光标
const setCursorPosition = (div, position) => {
  const textNode = div.childNodes[0];
  if (!textNode) {
    return;
  }
  const range = document.createRange();
  const sel = window.getSelection();
  range.setStart(textNode, position);
  range.setEnd(textNode, position);

  sel.removeAllRanges(); // 清除现有选区
  sel.addRange(range); // 添加新的选区
};

const ModifyTips = ({ tips }) => {
  return (
    <div className="flex mt-4px">
      <div className="flex items-center px-4px py-6px bg-black bg-opacity-4 rounded-6px">
        <SVGPureIcon name="plan_thinking" className="size-12px mr-3px" />
        <span className="text-12px text-black text-opacity-50">{tips}</span>
      </div>
    </div>
  );
};

const Outline = observer(
  ({
    items,
    item,
    index,
    parentIndex,
    disable,
    planStepId,
    itemType,
    event,
  }) => {
    const [canEdit, setCanEdit] = useState(false);
    const [isFocus, setIsFocus] = useState(false);
    const editDivRef = useRef(null);
    const isLast = index == items.length - 1;
    const editted = ['add', 'edit'].includes(item.status);
    const deleted = ['del'].includes(item.status);

    return (
      <Fragment key={index}>
        <div
          key={index}
          className={[
            'flex text-black text-14px border-b-1px border-black border-opacity-10',
            isLast ? '!border-b-0' : '',
          ]}
        >
          <div
            style={{ height: 'inherit' }}
            className="w-48px vh-center border-r-1px border-black border-opacity-10"
          >
            {numberToChinese(index + 1)}
          </div>
          <div
            className={[
              'flex flex-col justify-center px-12px py-9px flex-grow w-0 relative',
              canEdit ? 'plan-div-edit' : '',
            ]}
          >
            <div
              ref={editDivRef}
              suppressContentEditableWarning
              contentEditable={canEdit}
              onInput={evt => {
                evt.stopPropagation();
                const maxLength = 100;
                const div = evt.target;
                if (div.innerText.length > maxLength) {
                  div.innerText = div.innerText.slice(0, maxLength);
                  setCursorPosition(div, maxLength);
                }
              }}
              maxLength={10}
              onBlur={evt => {
                evt.stopPropagation();
                setTimeout(() => {
                  setCanEdit(false);
                  setIsFocus(false);
                }, 200);
                if (!editDivRef.current?.innerText?.trim()) {
                  //为空时做删除处理
                  const resItem = items.filter(_item => item.id != _item.id);
                  planQuestionStore.updatePlanData(
                    planStepId,
                    itemType,
                    parentIndex,
                    'items',
                    resItem
                  );
                  return;
                }
                const resItem = items.map(_item => {
                  runInAction(()=>{
                    if (item.id == _item.id) {
                      _item.text = editDivRef.current.innerText;
                    }
                  });
                  return _item;
                });
                planQuestionStore.updatePlanData(
                  planStepId,
                  itemType,
                  parentIndex,
                  'items',
                  resItem
                );
              }}
              onFocus={evt => {
                evt.stopPropagation();
                setIsFocus(true);
              }}
              onClick={evt => {
                if (event.status == 'running') {
                  evt.stopPropagation();
                }
              }}
              className={[
                'relative w-full leading-20px break-all border-1px border-transparent pr-74px',
              ]}
            >
              {editted || deleted ? (
                <span
                  className={[
                    'px-10px py-4px rounded-6px',
                    'leading-20px break-all inline-block',
                    {
                      'bg-[#FF7307] bg-opacity-20': editted,
                    },
                    {
                      'line-through text-[rgba(0,0,0,0.5)]': deleted,
                    },
                  ]}
                >
                  {item.text}
                </span>
              ) : (
                item.text
              )}
            </div>
            {editted || deleted ? (
              <ModifyTips tips={item.tips || '由模型反思后增加'} />
            ) : null}
            <div
              className={[
                'absolute right-12px top-1/2 transform -translate-y-1/2',
                'flex items-center mcp-mobile-hidden',
                isFocus || disable ? 'invisible' : '',
                canEdit ? 'invisible' : '',
              ]}
            >
              <div className="size-28px vh-center bg-black bg-opacity-0 hover:bg-opacity-3 rounded-6px">
                <SVGPureIcon
                  onClick={evt => {
                    evt.stopPropagation();
                    setCanEdit(true);
                    setTimeout(() => {
                      editDivRef.current?.focus();
                      setCursorPosition(editDivRef.current, item.text.length);
                    }, 20);
                  }}
                  name="plan_modify"
                  className="size-20px cursor-pointer"
                />
              </div>
              <div className="size-28px vh-center bg-black bg-opacity-0 hover:bg-opacity-3 rounded-6px">
                <SVGPureIcon
                  onClick={evt => {
                    evt.stopPropagation();
                    const resItem = items.filter(_item => item.id != _item.id);
                    planQuestionStore.updatePlanData(
                      planStepId,
                      itemType,
                      parentIndex,
                      'items',
                      resItem
                    );
                  }}
                  name="plan_delete"
                  className="size-20px cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
);
export default Outline;
